import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Download, Shield, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

export default function DataSnapshot() {
  const [loading, setLoading] = useState(false);
  const [snapshot, setSnapshot] = useState('');
  const [capturedAt, setCapturedAt] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get('/auth/me')
      .then(res => {
        if (!cancelled) {
          setIsAdmin((res.data?.role || '').toLowerCase() === 'admin');
          setAuthChecked(true);
        }
      })
      .catch(() => {
        if (!cancelled) setAuthChecked(true);
      });
    return () => { cancelled = true; };
  }, []);

  const fetchSnapshot = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/data-snapshot', { responseType: 'text' });
      setSnapshot(res.data);
      setCapturedAt(new Date());
      toast.success('Snapshot captured');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Failed to capture snapshot';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const downloadSnapshot = () => {
    if (!snapshot) return;
    const blob = new Blob([snapshot], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const ts = (capturedAt || new Date()).toISOString().replace(/[:.]/g, '-');
    a.href = url;
    a.download = `data-snapshot-${ts}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-[1100px] mx-auto px-6 space-y-6">
        {!authChecked && (
          <div className="flex items-center justify-center py-12 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Verifying access...
          </div>
        )}
        {authChecked && !isAdmin && (
          <Navigate to="/dashboard" replace />
        )}
        {authChecked && isAdmin && (<>
        {/* Header */}
        <div className="flex items-start gap-4">
          <div className="p-3 bg-indigo-100 rounded-lg">
            <Shield className="w-6 h-6 text-indigo-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Data Snapshot</h1>
            <p className="text-sm text-slate-600 mt-1">
              Capture a read-only snapshot of live data BEFORE and AFTER each redeployment.
              Diff the two snapshots to verify production data was preserved.
            </p>
          </div>
        </div>

        {/* Workflow Card */}
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <h2 className="text-base font-bold text-slate-900 mb-3">Recommended Workflow</h2>
          <ol className="text-sm text-slate-700 space-y-2 list-decimal list-inside">
            <li>Click <span className="font-mono bg-slate-100 px-2 py-0.5 rounded text-xs">Capture Snapshot</span> below — save the file as <code>before.txt</code>.</li>
            <li>Perform your redeployment via the Emergent platform.</li>
            <li>Return here, click <span className="font-mono bg-slate-100 px-2 py-0.5 rounded text-xs">Capture Snapshot</span> again — save as <code>after.txt</code>.</li>
            <li>Run <code className="bg-slate-100 px-2 py-0.5 rounded text-xs">diff before.txt after.txt</code> on your machine. Identical = production data preserved.</li>
            <li>Any unexpected difference (counts dropped, hash prefixes changed) = escalate to <a className="text-indigo-700 underline" href="mailto:support@emergent.sh">support@emergent.sh</a> with both snapshots attached.</li>
          </ol>
        </div>

        {/* Action Card */}
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-bold text-slate-900">Capture Snapshot</h2>
            <div className="flex gap-2">
              <button
                onClick={fetchSnapshot}
                disabled={loading}
                data-testid="capture-snapshot-btn"
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium text-sm disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                {loading ? 'Capturing...' : 'Capture Snapshot'}
              </button>
              <button
                onClick={downloadSnapshot}
                disabled={!snapshot}
                data-testid="download-snapshot-btn"
                className="flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-medium text-sm disabled:opacity-30"
              >
                <Download className="w-4 h-4" />
                Download .txt
              </button>
            </div>
          </div>

          {capturedAt && (
            <p className="text-xs text-slate-500 mb-3" data-testid="snapshot-timestamp">
              Captured: {capturedAt.toLocaleString()}
            </p>
          )}

          {snapshot ? (
            <pre
              data-testid="snapshot-content"
              className="bg-slate-900 text-slate-100 text-xs font-mono rounded-lg p-4 overflow-auto max-h-[500px] whitespace-pre"
            >{snapshot}</pre>
          ) : (
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
              <Shield className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">Click <strong>Capture Snapshot</strong> to capture the current state of live data.</p>
            </div>
          )}
        </div>

        {/* Warning Card */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
          <AlertCircle className="w-5 h-5 text-amber-700 flex-shrink-0" />
          <div className="text-xs text-amber-900 leading-relaxed">
            <p className="font-semibold mb-1">Read-Only Operation</p>
            <p>
              This endpoint never mutates data. It only reads collection counts, user email + bcrypt hash prefixes (never the full hash),
              and rate threshold samples. The snapshot is generated server-side and returned as plain text — nothing is stored on the server.
            </p>
          </div>
        </div>
        </>)}
      </div>
    </div>
  );
}
